import {
  Button,
  Checkbox,
  Container,
  Form,
  Heading,
  InnerForm,
  Modal,
  Table,
  Text,
  useModal,
} from "@fluidattacks/design";
import type { ColumnDef } from "@tanstack/react-table";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useTheme } from "styled-components";
import { boolean, object } from "yup";

import type { IDeleteFormValues } from "./types";

import { useDeleteMatchesDocsMutation } from "../hooks";
import type { TMatchDoc } from "../types";
import { useAudit } from "hooks/use-audit";
import { useAuthz } from "hooks/use-authz";
import { useTable } from "hooks/use-table";
import { filterDate } from "utils/date";
import { Logger } from "utils/logger";
import { useTableServerSideStore } from "utils/tables";
import type { TStore } from "utils/zustand";

interface IMatchesDocsTableProps {
  data: TMatchDoc[];
  loading: boolean;
  onAdd: () => void;
  groupName: string;
  refetch: () => void;
}

export const MatchesDocsTable = (
  props: IMatchesDocsTableProps,
): JSX.Element => {
  const { data, loading, onAdd, groupName, refetch } = props;
  const { t } = useTranslation();
  const theme = useTheme();
  const tableHelpers = useTableServerSideStore();
  const { canMutate } = useAuthz();
  const canDeleteDocs = canMutate("remove_matches_document");
  const { deleteMatchesDocsMutation, loading: deleteLoading } =
    useDeleteMatchesDocsMutation();
  const { addAuditEvent } = useAudit();

  const columns = useMemo(
    (): ColumnDef<TMatchDoc>[] => [
      {
        accessorKey: "fileName",
        header: t("group.matches.docsTable.file"),
        minSize: 400,
      },
      {
        accessorKey: "description",
        header: t("group.matches.docsTable.description"),
        minSize: 450,
      },
      {
        accessorKey: "modifiedDate",
        filterFn: filterDate,
        header: t("group.matches.docsTable.dateUploaded"),
        minSize: 300,
      },
    ],
    [t],
  );

  const { table } = useTable({
    columns,
    data,
    filters: tableHelpers as unknown as TStore<TMatchDoc, string>,
    id: "matches-docs-table",
    options: {
      filtersMode: "client-side",
      loading,
      paginationMode: "client-side",
      rowOptions: {
        enableMultiSelection: canDeleteDocs,
        getId: (item: TMatchDoc): string => item.fileName,
      },
    },
    total: data.length,
  });

  const { rowSelection } = table.getState();
  const { data: selectedData } = table.options.meta ?? {};

  const selectedFilenames = Object.keys(rowSelection)
    .map((key): TMatchDoc | undefined => selectedData?.get(key))
    .filter((doc): doc is TMatchDoc => doc !== undefined)
    .map((doc): string => doc.fileName);

  const deleteModalRef = useModal("delete-matches-docs-modal");
  const { open: openDeleteModal, close: closeDeleteModal } = deleteModalRef;

  const handleDelete = useCallback(
    async (values: IDeleteFormValues): Promise<void> => {
      if (values.confirmDelete) {
        try {
          await selectedFilenames.reduce(
            async (previousPromise, filename): Promise<void> => {
              await previousPromise;

              return deleteMatchesDocsMutation({
                filename,
                groupName,
              });
            },
            Promise.resolve(),
          );
          selectedFilenames.forEach((filename): void => {
            addAuditEvent("Matches.DeleteDocument", filename, groupName);
          });
          refetch();
        } catch (error: unknown) {
          Logger.error(`Error deleting matches docs`, error);
        }
        closeDeleteModal();
      }
    },
    [
      selectedFilenames,
      closeDeleteModal,
      deleteMatchesDocsMutation,
      groupName,
      addAuditEvent,
      refetch,
    ],
  );

  return (
    <Container
      alignItems={"left"}
      display={"flex"}
      flexDirection={"column"}
      gap={1.25}
      width={"100%"}
    >
      <Heading
        fontWeight={"bold"}
        size={"sm"}
        textAlign={"left"}
        wordBreak={"break-word"}
      >
        {t("group.matches.docsTable.header")}
      </Heading>
      <Text size={"md"} textAlign={"left"} wordBreak={"break-word"}>
        {t("group.matches.docsTable.info")}
      </Text>
      <Table table={table}>
        <Table.Options>
          <Table.Controls>
            <Container gap={0.5} id={"actions-container"}>
              <Button
                disabled={selectedFilenames.length === 0 || deleteLoading}
                icon={"trash-alt"}
                id={"deleteMatchesFile"}
                onClick={openDeleteModal}
                variant={"ghost"}
                visibility={canDeleteDocs ? "visible" : "hidden"}
              >
                {t("group.matches.docsTable.delete")}
              </Button>
              <Button icon={"plus"} id={"addMatchesFile"} onClick={onAdd}>
                {t("group.matches.docsTable.add")}
              </Button>
            </Container>
          </Table.Controls>
        </Table.Options>
        <Table.Content />
        <Table.Footer />
      </Table>
      <Modal
        id={"delete-docs-modal"}
        modalRef={{ ...deleteModalRef, close: closeDeleteModal }}
        size={"sm"}
        title={t("group.matches.docsTable.deleteModal.title")}
      >
        <Form<IDeleteFormValues>
          cancelButton={{ onClick: closeDeleteModal }}
          confirmButton={{ disabled: false }}
          defaultValues={{ confirmDelete: false }}
          onSubmit={handleDelete}
          yupSchema={object().shape({
            confirmDelete: boolean().oneOf([true], t("validations.required")),
          })}
        >
          <InnerForm>
            {({
              register,
            }: {
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              register: (name: string) => any;
            }): JSX.Element => (
              <Container display={"flex"} flexDirection={"column"} gap={1}>
                <Text size={"md"}>
                  {t("group.matches.docsTable.deleteModal.message")}
                </Text>
                <Container display={"flex"} flexDirection={"column"} gap={0.5}>
                  {selectedFilenames.map(
                    (doc): JSX.Element => (
                      <Text fontWeight={"bold"} key={doc} size={"md"}>
                        {"• "}
                        {doc}
                      </Text>
                    ),
                  )}
                </Container>
                <Checkbox
                  {...register("confirmDelete")}
                  label={t(
                    "group.matches.docsTable.deleteModal.confirmCheckbox",
                  )}
                  name={"confirmDelete"}
                />
                <Text
                  color={theme.palette.gray[400]}
                  fontStyle={"italic"}
                  size={"md"}
                >
                  {t("group.matches.docsTable.deleteModal.note")}
                </Text>
              </Container>
            )}
          </InnerForm>
        </Form>
      </Modal>
    </Container>
  );
};
